import { ParsedData, Example, ModelResponse, Entity, LabelDefinition } from '../types';
import { validateAgainstSchema } from './schemaValidator';

export function parseNERFile(content: string): ParsedData {
  console.log('Parsing file content, length:', content.length);

  const lines = content.split('\n');
  const examples: Example[] = [];
  const modelNames = new Set<string>();
  let currentModelName: string | null = null;
  let currentExample: Example | null = null;
  let exampleCounter = 0;
  let pendingInferenceTime: number | undefined = undefined;

  // Track all examples across models to group them
  const exampleMap = new Map<string, Example>();

  console.log('Total lines:', lines.length);

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Skip empty lines and system messages
    if (!line ||
        line.startsWith('Asking to truncate') ||
        line.startsWith('You seem to be using')) {
      continue;
    }

    // Check for inference time
    if (line.toLowerCase().startsWith('inference time:')) {
      const timeMatch = line.match(/inference time:\s*([\d.]+)/i);
      if (timeMatch) {
        pendingInferenceTime = parseFloat(timeMatch[1]);
        console.log('Found inference time:', pendingInferenceTime);
      }
      continue;
    }

    // Check for model separator
    if (line === '====================') {
      console.log('Found model separator');
      currentModelName = null;
      currentExample = null;
      continue;
    }

    // Check for example separator
    if (line === '----------') {
      currentExample = null;
      continue;
    }

    // Check for model name
    if (line.startsWith('Model:')) {
      currentModelName = line.substring(6).trim();
      console.log('Found model:', currentModelName);
      modelNames.add(currentModelName);
      currentExample = null;
      continue;
    }

    // Check for example
    if (line.startsWith('Example:')) {
      const exampleText = line.substring(8).trim();
      console.log('Found example:', exampleText);

      // Find or create example
      if (exampleMap.has(exampleText)) {
        currentExample = exampleMap.get(exampleText)!;
        console.log('Reusing existing example');
      } else {
        currentExample = {
          id: `q${++exampleCounter}`,
          text: exampleText,
          modelResponses: []
        };
        exampleMap.set(exampleText, currentExample);
        examples.push(currentExample);
        console.log('Created new example with id:', currentExample.id);
      }
      continue;
    }

    // Check for entity (format: "text - label" or "text - label - (start, end) - [score]")
    if (currentExample && currentModelName && line.includes(' - ')) {
      // Split by " - " to get components
      const parts = line.split(' - ').map(p => p.trim());

      if (parts.length < 2) continue;

      const entityText = parts[0];
      const label = parts[1];
      let start: number | undefined;
      let end: number | undefined;
      let confidence: number | undefined;

      // Check for position info: (start, end)
      if (parts.length >= 3) {
        const posMatch = parts[2].match(/^\((\d+),\s*(\d+)\)$/);
        if (posMatch) {
          start = parseInt(posMatch[1], 10);
          end = parseInt(posMatch[2], 10);
        }
      }

      // Check for score info: [score]
      if (parts.length >= 4) {
        const scoreMatch = parts[3].match(/^\[([0-9.eE+-]+)\]$/);
        if (scoreMatch) {
          confidence = parseFloat(scoreMatch[1]);
        }
      }

      // Find or create model response for this example
      let modelResponse = currentExample.modelResponses.find(
        mr => mr.modelName === currentModelName
      );

      if (!modelResponse) {
        modelResponse = {
          modelName: currentModelName,
          entities: [],
          inferenceTime: pendingInferenceTime
        };
        currentExample.modelResponses.push(modelResponse);
        console.log('Created model response for', currentModelName, 'in example', currentExample.id, 'with inference time:', pendingInferenceTime);
        pendingInferenceTime = undefined; // Reset after using
      }

      const entity: Entity = {
        text: entityText,
        label: label,
        start,
        end,
        confidence
      };
      modelResponse.entities.push(entity);
      console.log('Added entity:', entityText, '-', label, start !== undefined ? `(${start},${end})` : '', confidence !== undefined ? `[${confidence}]` : '');
    }
  }

  console.log('Parse complete. Total examples:', examples.length);
  console.log('Model names found:', Array.from(modelNames));

  // Log statistics for each example
  examples.forEach(q => {
    console.log(`Example ${q.id}: ${q.modelResponses.length} model responses`);
  });

  return {
    examples,
    modelNames: Array.from(modelNames)
  };
}

// Parse JSON export format (model_results_*.json)
// Accepts both new ('examples', 'confidence') and legacy ('questions', 'score') keys
// for one release cycle. When both are present on a record, the new key wins.
export function parseNERJson(content: string): ParsedData {
  const raw = JSON.parse(content);

  const examples: Example[] = [];
  const modelNames = new Set<string>();

  // Accept both new 'examples' and legacy 'questions' top-level arrays.
  const sourceArray = Array.isArray(raw?.examples)
    ? raw.examples
    : (Array.isArray(raw?.questions) ? raw.questions : null);

  if (!sourceArray) {
    throw new Error('Invalid JSON format: missing examples array');
  }

  sourceArray.forEach((q: any, idx: number) => {
    const exampleId: string = q.id || `q${idx + 1}`;
    const text: string = q.text || '';

    // Human annotations (optional)
    const humanAnnSrc: any[] = q.humanAnnotations || q.annotations || q.gold || [];
    const humanAnnotations: Entity[] = Array.isArray(humanAnnSrc)
      ? humanAnnSrc.map((e: any) => ({
          text: String(e.text ?? ''),
          label: String(e.label ?? ''),
          start: typeof e.start === 'number' ? e.start : undefined,
          end: typeof e.end === 'number' ? e.end : undefined,
          // Prefer new 'confidence' over legacy 'score' when both present
          confidence: typeof e.confidence === 'number' ? e.confidence : (typeof e.score === 'number' ? e.score : undefined)
        }))
      : [];

    // Support both 'modelResponses' (new schema) and 'models' (legacy)
    const modelResponsesArray = q.modelResponses || q.models || [];
    const modelResponses: ModelResponse[] = Array.isArray(modelResponsesArray)
      ? modelResponsesArray.map((m: any) => {
          const name: string = m.modelName || 'Unknown Model';
          modelNames.add(name);
          const entities: Entity[] = Array.isArray(m.entities)
            ? m.entities.map((e: any) => ({
                text: String(e.text ?? ''),
                label: String(e.label ?? ''),
                start: typeof e.start === 'number' ? e.start : undefined,
                end: typeof e.end === 'number' ? e.end : undefined,
                // Prefer new 'confidence' over legacy 'score' when both present
                confidence: typeof e.confidence === 'number' ? e.confidence : (typeof e.score === 'number' ? e.score : undefined)
              }))
            : [];

          const response: ModelResponse = {
            modelName: name,
            entities,
            inferenceTime: typeof m.inferenceTime === 'number' ? m.inferenceTime : undefined
          };
          return response;
        })
      : [];

    // BQ-15: rejected predictions (optional). Each entry pairs a modelName
    // with the predicted entity that was dismissed.
    const rejectedSrc: any[] = Array.isArray(q.rejectedPredictions) ? q.rejectedPredictions : [];
    const rejectedPredictions = rejectedSrc
      .filter((r: any) => r && typeof r.modelName === 'string' && r.entity && typeof r.entity === 'object')
      .map((r: any) => ({
        modelName: String(r.modelName),
        entity: {
          text: String(r.entity.text ?? ''),
          label: String(r.entity.label ?? ''),
          start: typeof r.entity.start === 'number' ? r.entity.start : undefined,
          end: typeof r.entity.end === 'number' ? r.entity.end : undefined,
          confidence: typeof r.entity.confidence === 'number' ? r.entity.confidence : undefined,
        },
      }));

    examples.push({ id: exampleId, text, modelResponses, humanAnnotations, rejectedPredictions });
  });

  // Use top-level modelNames if provided, otherwise use discovered names
  if (raw.modelNames && Array.isArray(raw.modelNames)) {
    raw.modelNames.forEach((name: any) => {
      if (typeof name === 'string') modelNames.add(name);
    });
  }

  // If no model names were discovered, fall back to metadata.models
  if (modelNames.size === 0 && raw && raw.metadata && Array.isArray(raw.metadata.models)) {
    raw.metadata.models.forEach((m: any) => {
      if (typeof m === 'string') modelNames.add(m);
    });
  }

  // Parse custom label colors if provided
  const customLabelColors: Record<string, string> | undefined =
    raw.customLabelColors && typeof raw.customLabelColors === 'object'
      ? raw.customLabelColors
      : undefined;

  // Parse saved themes if provided
  const savedThemes = raw.savedThemes && Array.isArray(raw.savedThemes)
    ? raw.savedThemes.filter((t: any) => t.id && t.name && t.bgColor && t.textColor && t.borderColor)
    : undefined;

  // BQ-18: per-label annotation guidelines (optional). Validate lightly —
  // accept only objects with string keys whose values are objects with
  // optional `description` (string) / `examples` (string[]) /
  // `counterExamples` (string[]). Anything else is silently skipped so a
  // malformed entry can't crash the loader.
  let labelDefinitions: Record<string, LabelDefinition> | undefined;
  if (raw.labelDefinitions && typeof raw.labelDefinitions === 'object' && !Array.isArray(raw.labelDefinitions)) {
    const out: Record<string, LabelDefinition> = {};
    for (const [k, v] of Object.entries(raw.labelDefinitions as Record<string, unknown>)) {
      if (typeof k !== 'string' || !v || typeof v !== 'object' || Array.isArray(v)) continue;
      const def = v as Record<string, unknown>;
      const entry: LabelDefinition = {};
      if (typeof def.description === 'string') {
        entry.description = def.description;
      }
      if (Array.isArray(def.examples)) {
        entry.examples = def.examples.filter((s): s is string => typeof s === 'string');
      }
      if (Array.isArray(def.counterExamples)) {
        entry.counterExamples = def.counterExamples.filter((s): s is string => typeof s === 'string');
      }
      // Only keep the entry if at least one field is non-empty.
      const isEmpty =
        !entry.description?.trim() &&
        (entry.examples?.length ?? 0) === 0 &&
        (entry.counterExamples?.length ?? 0) === 0;
      if (!isEmpty) out[k] = entry;
    }
    if (Object.keys(out).length > 0) {
      labelDefinitions = out;
    }
  }

  return {
    examples,
    modelNames: Array.from(modelNames),
    customLabelColors,
    savedThemes,
    labelDefinitions,
  };
}

// Auto-detect between text format and JSON export.
//
// BQ-22: when the input looks like JSON we now run an Ajv pass against
// `/data-schema.json` *before* parsing, so users uploading a malformed
// payload get a useful path-keyed error instead of partial silent loads.
// `parseNERContent` is therefore async; the only call site (App.tsx) awaits
// it. The legacy text path remains synchronous internally (we just resolve
// the promise immediately).
export async function parseNERContent(content: string, fileName?: string): Promise<ParsedData> {
  const trimmed = content.trim();
  const isLikelyJson = (fileName && fileName.toLowerCase().endsWith('.json')) || trimmed.startsWith('{') || trimmed.startsWith('[');
  if (isLikelyJson) {
    let parsedJson: unknown;
    try {
      parsedJson = JSON.parse(content);
    } catch (e) {
      // Bad JSON syntax — fall through to legacy text parser as today.
      return parseNERFile(content);
    }

    const result = await validateAgainstSchema(parsedJson);
    if (!result.valid) {
      // Cap the joined message so a totally unrelated file doesn't dump 200
      // errors into the alert. The first 5 are the most actionable.
      const limit = 5;
      const head = result.errors.slice(0, limit).map((e) => `${e.path} ${e.message}`);
      const remainder = result.errors.length - head.length;
      const tail = remainder > 0 ? `; ...${remainder} more` : '';
      throw new Error(`Schema validation failed: ${head.join('; ')}${tail}`);
    }

    return parseNERJson(content);
  }
  return parseNERFile(content);
}


// getEntityColorClass moved to ./labelColor (BQ-14).
